import { meQueryKey, useUser } from "@/lib/Auth/useUser";
import { useAuthStore } from "@/lib/Auth/authStore";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  cancelEmailChangeMutation,
  requestEmailChangeMutation,
  updateMeMutation,
  updateMyNotificationSettingsMutation,
  updateMyProfilePictureMutation,
  verifyEmailChangeMutation,
} from "@/app/Profile/GraphQL/profileMutations";
import { Switch, SwitchField } from "@/components/catalyst/switch";
import { Description, Label } from "@/components/catalyst/fieldset";
import { MeResponse, setPassword as setPasswordApi } from "@/lib/Auth/authApi";
import {
  SettingCard,
  SettingFormCard,
} from "@/app/Settings/Settings/Components/SettingFormCard";
import {
  EditableRowsProvider,
  EditableSettingRow,
  SettingRowsCard,
} from "@/app/Settings/Settings/Components/EditableSettingRow";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { DateOfBirthInput } from "@/lib/Components/Form/Inputs/DateOfBirthInput";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { z } from "zod";
import { Avatar } from "@/components/catalyst/avatar";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { FileUploader } from "@/lib/FileUpload/FileUploader";
import { Dialog, DialogTitle } from "@/components/catalyst/dialog";
import { useState } from "react";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/button";
import { Badge } from "@/components/catalyst/badge";
import { getInitials } from "@/lib/Utils/getInitials";
import { useTranslations } from "use-intl";

const phoneSchema = z.object({
  primary_phone: z.string().nullable(),
});

const dateOfBirthSchema = z.object({
  date_of_birth: z.string().nullable(),
});

export function ProfileScreen() {
  const t = useTranslations("profile");

  return (
    <div className="scroll-gutter-stable h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto w-full max-w-3xl space-y-12 px-4 py-12 sm:px-6 lg:px-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
          <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        </div>

        <EditableRowsProvider>
          <div className="space-y-12">
            <PendingEmailCard />
            <PersonalDetailsCard />
            <NotificationsCard />
            <PasswordCard />
          </div>
        </EditableRowsProvider>
      </div>
    </div>
  );
}

/** The /me query resolves to null while signed out. */
type MeQueryData = MeResponse | null;

function NotificationsCard() {
  const t = useTranslations("profile.notifications");
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutate } = useGqlMutation(updateMyNotificationSettingsMutation, {
    /** Toggling should feel instant; the global post-mutation invalidation refetches /me. */
    onMutate: async (variables: any) => {
      await queryClient.cancelQueries({ queryKey: meQueryKey });

      const previous = queryClient.getQueryData<MeQueryData>(meQueryKey);

      queryClient.setQueryData<MeQueryData>(meQueryKey, (old) =>
        old
          ? {
              ...old,
              user: {
                ...old.user,
                notification_settings: {
                  ...old.user.notification_settings,
                  gets_email_notifications: variables.gets_email_notifications,
                },
              },
            }
          : old,
      );

      return { previous };
    },
    onError: (_err: any, _vars: any, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(meQueryKey, context.previous);
      }
      toast.error(t("updateError"));
    },
  });

  return (
    <SettingCard title={t("title")} description={t("description")}>
      <SwitchField>
        <Label>{t("email.label")}</Label>
        <Description>{t("email.description")}</Description>
        <Switch
          className="row-span-2 row-start-1"
          checked={user.notification_settings.gets_email_notifications}
          onChange={(checked) => {
            mutate({ gets_email_notifications: checked });
          }}
        />
      </SwitchField>
    </SettingCard>
  );
}

function ProfilePictureRow() {
  const t = useTranslations("profile.details.picture");
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync: updateProfilePicture } = useGqlMutation(
    updateMyProfilePictureMutation,
  );
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  return (
    <div className="flex items-center gap-4 px-4 py-3 sm:px-6">
      <div className="w-40 shrink-0 text-sm font-medium text-gray-500">
        {t("label")}
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-x-4">
        {user?.profile_picture_url ? (
          <Avatar
            src={user.profile_picture_url}
            initials={user.name ? getInitials(user.name) : undefined}
            className="size-12"
          />
        ) : (
          <UserCircleIcon
            className="size-12 text-gray-300"
            aria-hidden="true"
          />
        )}
        <Button outline onClick={() => setOpen(true)}>
          {user?.profile_picture_url ? t("change") : t("upload")}
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>{t("dialogTitle")}</DialogTitle>
          <div className="relative w-full py-1">
            <FileUploader
              onDrop={() => setIsUploading(true)}
              onUpload={async (uploads) => {
                if (uploads.length !== 1) {
                  throw new Error(t("singleFileError"));
                }
                const upload = uploads[0];

                await updateProfilePicture({ key: upload.filePath });
                await queryClient.invalidateQueries({
                  queryKey: ["auth", "me"],
                });

                setOpen(false);
                setIsUploading(false);
                toast.success(t("updated"));
              }}
            />
            {isUploading ? (
              <div className="backdrop-blur-xs absolute inset-0 flex items-center justify-center bg-white/30">
                <Spinner />
              </div>
            ) : null}
          </div>
        </Dialog>
      </div>
    </div>
  );
}

function PersonalDetailsCard() {
  const t = useTranslations("profile.details");
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync } = useGqlMutation(updateMeMutation);

  const nameSchema = z.object({
    name: z.string().min(1, t("name.requiredError")),
  });

  async function save(input: {
    name?: string;
    primary_phone?: string;
    date_of_birth?: string | null;
  }) {
    await mutateAsync({ input });
    await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  }

  return (
    <SettingRowsCard title={t("title")} description={t("description")}>
      <ProfilePictureRow />
      <EditableSettingRow
        label={t("name.label")}
        value={user?.name}
        initialValues={{ name: user?.name ?? "" }}
        validationSchema={toFormikValidationSchema(nameSchema)}
        onSave={async (values) => save({ name: values.name })}
      >
        <TextInput name="name" label={t("name.label")} />
      </EditableSettingRow>
      <EmailRow />
      <EditableSettingRow
        label={t("phone.label")}
        value={user?.primary_phone}
        emptyLabel={t("optional")}
        initialValues={{ primary_phone: user?.primary_phone ?? "" }}
        validationSchema={toFormikValidationSchema(phoneSchema)}
        onSave={async (values) => save({ primary_phone: values.primary_phone })}
      >
        <TextInput name="primary_phone" label={t("phone.label")} />
      </EditableSettingRow>
      <EditableSettingRow
        label={t("dateOfBirth.label")}
        value={user?.date_of_birth}
        emptyLabel={t("optional")}
        initialValues={{ date_of_birth: user?.date_of_birth ?? null }}
        validationSchema={toFormikValidationSchema(dateOfBirthSchema)}
        onSave={async (values) => save({ date_of_birth: values.date_of_birth })}
      >
        <DateOfBirthInput name="date_of_birth" label={t("dateOfBirth.label")} />
      </EditableSettingRow>
    </SettingRowsCard>
  );
}

/** Only rendered mid email-change: the code entry needs a full card of its own. */
function PendingEmailCard() {
  const t = useTranslations("profile.pendingEmail");
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync: verifyChange } = useGqlMutation(
    verifyEmailChangeMutation,
  );
  const { mutateAsync: cancelChange } = useGqlMutation(
    cancelEmailChangeMutation,
  );

  const verifyCodeSchema = z.object({
    code: z.string().min(1, t("codeRequiredError")),
  });

  if (!user?.pending_email) {
    return null;
  }

  return (
    <div className="space-y-4">
      <SettingFormCard
        title={
          <span className="flex items-center gap-2">
            {t("title")}
            <Badge color="amber">{t("badge")}</Badge>
          </span>
        }
        description={t("description", { email: user.pending_email })}
        initialValues={{ code: "" }}
        onSubmit={async (values) => {
          await verifyChange({ code: values.code });
          await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
        }}
        validationSchema={toFormikValidationSchema(verifyCodeSchema)}
      >
        <TextInput name="code" label={t("codeLabel")} className="col-span-4" />
        <div className="col-span-full">
          <Button
            plain
            type="button"
            onClick={async () => {
              await cancelChange({});
              await queryClient.invalidateQueries({
                queryKey: ["auth", "me"],
              });
              toast.success(t("cancelled"));
            }}
          >
            {t("cancel")}
          </Button>
        </div>
      </SettingFormCard>
    </div>
  );
}

function EmailRow() {
  const t = useTranslations("profile.details.email");
  const user = useUser();
  const queryClient = useQueryClient();
  const { mutateAsync: requestChange } = useGqlMutation(
    requestEmailChangeMutation,
  );

  const emailSchema = z.object({
    email: z.email(t("invalidError")),
  });

  if (user?.pending_email) {
    return null;
  }

  return (
    <EditableSettingRow
      label={t("label")}
      value={user?.email}
      savedMessage={t("codeSent")}
      initialValues={{ email: user?.email ?? "" }}
      validationSchema={toFormikValidationSchema(emailSchema)}
      onSave={async (values) => {
        await requestChange({ email: values.email });
        await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      }}
    >
      <TextInput type="email" name="email" label={t("label")} />
    </EditableSettingRow>
  );
}

function PasswordCard() {
  const t = useTranslations("profile.password");
  const user = useUser();
  const { token } = useAuthStore();
  const queryClient = useQueryClient();

  const passwordSchema = z
    .object({
      password: z.string().min(8, t("minLengthError")),
      password_confirmation: z.string().min(8, t("confirmError")),
    })
    .refine((data) => data.password === data.password_confirmation, {
      message: t("mismatchError"),
      path: ["password_confirmation"],
    });

  const { mutateAsync: submitPassword } = useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      if (!token) throw new Error(t("notAuthenticatedError"));
      await setPasswordApi(token, password);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  return (
    <SettingRowsCard
      title={t("title")}
      description={
        user?.has_password ? t("updateDescription") : t("setDescription")
      }
    >
      <EditableSettingRow
        label={t("label")}
        value={user?.has_password ? "••••••••" : null}
        editClassName="max-w-2xl"
        savedMessage={user?.has_password ? t("updated") : t("set")}
        initialValues={{ password: "", password_confirmation: "" }}
        validationSchema={toFormikValidationSchema(passwordSchema)}
        onSave={async (values) => {
          await submitPassword({ password: values.password });
        }}
      >
        <TextInput
          type="password"
          name="password"
          label={user?.has_password ? t("newLabel") : t("label")}
        />
        <TextInput
          type="password"
          name="password_confirmation"
          label={t("confirmLabel")}
        />
      </EditableSettingRow>
    </SettingRowsCard>
  );
}
